import { BellIcon } from '@phosphor-icons/react';

import { EmptyState } from '@/components/feedback/empty-state';
import { ErrorState } from '@/components/feedback/error-state';

import { formatNotificationTime } from '../format';
import type { AppNotification } from '../types';

import styles from './notification-panel.module.scss';
import { NotificationRowSkeleton } from './notification-row-skeleton';

/** Newest first; ties broken by id desc (matches the backend list contract). */
function sortNewestFirst(items: AppNotification[]): AppNotification[] {
  return [...items].sort((a, b) => {
    const byTime = b.timestamp.localeCompare(a.timestamp);
    return byTime !== 0 ? byTime : b.id.localeCompare(a.id);
  });
}

export type NotificationPanelProps = {
  notifications: AppNotification[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (n: AppNotification) => void;
};

/**
 * Dropdown body: header + scrollable list. Unread rows sit on a cream rounded card with
 * a dark-green bell avatar + orange unread dot; read rows are plain with a grey avatar.
 */
export function NotificationPanel({
  notifications,
  isLoading,
  isError,
  onRetry,
  onSelect,
}: NotificationPanelProps) {
  const items = sortNewestFirst(notifications);

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <h2 className={styles.heading}>Notifications</h2>
      </header>

      <div className={styles.body}>
        {isError ? (
          <ErrorState
            title="Unable to open notifications"
            description="Please try again later."
            onRetry={onRetry}
          />
        ) : isLoading ? (
          <div className={styles.loading}>
            <NotificationRowSkeleton count={5} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon={<BellIcon size={40} weight="duotone" />} title="No notifications" />
        ) : (
          <ul className={styles.list}>
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={n.read ? styles.row : `${styles.row} ${styles.unread}`}
                  onClick={() => onSelect(n)}
                >
                  <span
                    className={n.read ? styles.avatar : `${styles.avatar} ${styles.avatarUnread}`}
                    aria-hidden="true"
                  >
                    <BellIcon size={16} weight="regular" />
                    {!n.read ? <span className={styles.unreadDot} /> : null}
                  </span>
                  <span className={styles.content}>
                    <span className={styles.title}>{n.title}</span>
                    <span className={styles.bodyText}>{n.body}</span>
                  </span>
                  <time className={styles.time} dateTime={n.timestamp}>
                    {formatNotificationTime(n.timestamp)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
